import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField } from '@mui/material'
import React from 'react'
import GrantContent from '../../../components/GrantContent'
import type { MonedaFormato } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import * as Permisos from '../../../utils/Permisos'
import { FORMAT_OPTIONS } from '../common/currency-common'
import { useCurrencyFormModel } from './currency-form-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }
const dialogBtnClass = 'inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40'

const CurrencyForm = () => {
    const model = useCurrencyFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar moneda</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la moneda {form.moneda}? Esta acción es irreversible.</p>
                </DialogContent>
                <DialogActions className="gap-1 px-6 pb-5">
                    <button type="button" className={`${dialogBtnClass} border border-k-line bg-k-surface text-k-ink hover:bg-k-hover`} onClick={model.cancelDelete}>Cancelar</button>
                    <button type="button" className={`${dialogBtnClass} border-0 bg-k-danger font-bold !text-white hover:brightness-110`} onClick={model.deleteCurrency} disabled={model.deleting}>Eliminar</button>
                </DialogActions>
            </Dialog>

            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description="Configura el tipo de cambio y el formato con que se muestran los importes en esta moneda."
                    backTo={model.backTo}
                    backLabel="Monedas"
                    actions={model.isEdit ? (
                        <GrantContent permisos={[Permisos.MONEDA_D]}>
                            <button
                                type="button"
                                className="inline-flex h-10 items-center gap-2 rounded-xl border-0 bg-k-danger px-4 text-sm font-bold !text-white hover:brightness-110 disabled:opacity-40"
                                onClick={model.askDelete}
                                disabled={model.deleting}
                            >
                                <DeleteIcon style={{ fontSize: 18 }} />
                                Eliminar
                            </button>
                        </GrantContent>
                    ) : (
                        <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Moneda</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField
                            className="v2-field v2-select md:col-span-2" variant="filled" fullWidth select required
                            label="Moneda"
                            disabled={model.isEdit}
                            helperText={model.isEdit ? 'Para usar otra moneda, agrégala como nueva.' : 'Las monedas que ya configuraste aparecen deshabilitadas.'}
                            value={form.moneda}
                            onChange={(event) => change.moneda(event.target.value)}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                        >
                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                            {model.options.map((option) => (
                                <MenuItem key={option.code} value={option.code} disabled={option.configured}>
                                    {option.label}{option.configured ? ' · ya configurada' : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            className="v2-field" variant="filled" fullWidth required
                            label="Tipo de cambio"
                            helperText="Pesos mexicanos por una unidad de esta moneda."
                            value={form.tipoCambio}
                            onChange={(event) => change.tipoCambio(event.target.value)}
                            onBlur={(event) => change.tipoCambioBlur(event.target.value)}
                            onFocus={(event) => event.target.select()}
                            slotProps={{
                                htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } },
                                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                            }}
                        />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth select required
                            label="Formato"
                            helperText={model.example ? `Así se mostrarán los importes: ${model.example}` : undefined}
                            value={form.formato}
                            onChange={(event) => change.formato(event.target.value as MonedaFormato)}
                            slotProps={{ select: selectMenu }}
                        >
                            {FORMAT_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.example} · {option.label}</MenuItem>
                            ))}
                        </TextField>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.busy || !form.moneda}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar moneda
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default CurrencyForm
